import classNames from 'classnames';
import { gsap } from 'gsap';
import { useEffect, useRef } from 'react';

import LatexString from 'my-elements/LatexString';

import Typography from '@mui/material/Typography';

const rootCss = ({ breakpoints, palette, spacing, typography }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: palette.primary.background,
  zIndex: 1,
  '.CLVT-content': {
    overflow: 'hidden',
    textAlign: 'center',
  },
  '.CLVT-svg': {
    width: 150,
    margin: spacing(1),
  },
  '.CLVT-badge': {
    fill: palette.primary.main,
    visibility: 'none',
    opacity: 0,
  },
  '.CLVT-wize': {
    fill: palette.common.white,
    visibility: 'none',
    opacity: 0,
  },
  '.CLVT-message': {
    margin: spacing(1),
    fontSize: typography.h4.fontSize,
  },

  [breakpoints.only('xs')]: {
    '.CLVT-svg': { width: '20vw' },
    '.CLVT-message': { fontSize: '4.5vw' },
  },
});

export default function CourseLessonVideoTransition({
  badgeClassName,
  className,
  contentClassName,
  message,
  messageClassName,
  svgClassName,
  wizeClassName,
}) {
  const rootRef = useRef(null);
  const badgeRef = useRef(null);
  const wizeRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const badge = badgeRef.current;
    const wize = wizeRef.current;

    const tl = new gsap.timeline();
    tl.set([badge, wize], { autoAlpha: 0, scale: 0, transformOrigin: '50% 50%' })
      .set(root, { autoAlpha: 1 })
      .to(badge, {
        duration: 1,
        autoAlpha: 1,
        rotation: 360,
        scale: 0.9,
        transformOrigin: '50% 50%',
        delay: 0.75,
        ease: 'back.out(1)',
      })
      .to(
        wize,
        {
          duration: 0.5,
          autoAlpha: 1,
          scale: 1,
          transformOrigin: '50% 50%',
        },
        '-=.75',
      )
      .to(root, { duration: 0.5, autoAlpha: 0 }, '+=.75')
      .duration(2.5);

    return () => tl.kill();
  }, []);

  return (
    <div className={className} css={rootCss} ref={rootRef}>
      <div className={classNames('CLVT-content', contentClassName)}>
        <svg
          className={classNames('CLVT-svg', svgClassName)}
          viewBox="0 0 46.64 46.21"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            className={classNames('CLVT-badge', badgeClassName)}
            d="M46.64 23.1c0 1.11-1 2.13-1.14 3.19s.5 2.35.2 3.38-1.54 1.76-2 2.74-.18 2.39-.77 3.3-2 1.24-2.69 2.06-.84 2.24-1.66 3-2.24.63-3.16 1.22-1.43 1.91-2.42 2.37-2.32 0-3.38.28-1.9 1.43-3 1.59-2.22-.68-3.32-.68-2.26.83-3.32.68-2-1.28-3-1.59-2.39.17-3.37-.28-1.51-1.78-2.43-2.37-2.33-.51-3.15-1.23-.93-2.17-1.67-2.99-2.1-1.14-2.69-2.06-.31-2.31-.74-3.3-1.68-1.69-2-2.74.35-2.29.2-3.38S0 24.21 0 23.1s1-2.13 1.14-3.19-.5-2.35-.2-3.38 1.54-1.76 2-2.74.18-2.39.77-3.3 2-1.24 2.69-2.06.84-2.24 1.66-3 2.24-.63 3.16-1.22 1.43-1.91 2.42-2.37 2.32 0 3.37-.28 1.91-1.43 3-1.59 2.21.68 3.32.68 2.25-.79 3.31-.64 2 1.28 3 1.59 2.39-.17 3.37.28 1.51 1.78 2.43 2.37 2.33.51 3.15 1.23 1 2.13 1.66 3 2.1 1.14 2.69 2.06.31 2.31.77 3.3 1.68 1.69 2 2.74-.35 2.29-.2 3.38 1.13 2.03 1.13 3.14z"
            ref={badgeRef}
          />
          <path
            className={classNames('CLVT-wize', wizeClassName)}
            d="M23.91 14.02h1l-2.82 8.48h-2.21l-1.31-3.73h1.19a1 1 0 0 1 1 .7l.32.95 1.93-5.71a1 1 0 0 1 .9-.69zM27.71 14.71l-1.92 5.71-.32-.95a1 1 0 0 0-1-.69h-1.11l1.31 3.72h2.22l2.82-8.48h-1a1 1 0 0 0-1 .69zM18.07 14.02h-1.18l.72 2a1.21 1.21 0 0 0 1.14.8h1.05l-.78-2.18a1 1 0 0 0-.95-.62zM11.18 27.29h.54a.66.66 0 0 1 .62.46l.93 3.1 1-3.11a.65.65 0 0 1 .62-.45h.1a.55.55 0 0 1 .52.37l1.08 3.18.88-3.09a.65.65 0 0 1 .62-.46h.48l-1.52 4.81h-.91l-1.22-3.6a.05.05 0 0 0-.08 0l-1.13 3.6h-1zM21.22 27.29v3.86a.9.9 0 0 0 .91.9v-3.86a.9.9 0 0 0-.91-.9zM24.74 31.24l2.7-3.14h-1.83a.78.78 0 0 1-.78-.78h3.71v.78l-2.72 3.18h2.08a.78.78 0 0 1 .78.78h-3.94zM31.77 30a1.26 1.26 0 0 0 .14.57 1.39 1.39 0 0 0 .36.44 1.8 1.8 0 0 0 .51.28 2 2 0 0 0 .6.1 1.34 1.34 0 0 0 .73-.2l.13-.08a.81.81 0 0 1 1 0l.13.09a2.51 2.51 0 0 1-2.11 1 2.75 2.75 0 0 1-1-.19 2.26 2.26 0 0 1-.77-.53 2.33 2.33 0 0 1-.49-.79 3.08 3.08 0 0 1-.17-1 2.65 2.65 0 0 1 .19-1 2.52 2.52 0 0 1 .5-.79 2.4 2.4 0 0 1 .78-.53 2.6 2.6 0 0 1 1-.19 2.34 2.34 0 0 1 1.07.22 2.16 2.16 0 0 1 .73.58 2.41 2.41 0 0 1 .4.8 3.13 3.13 0 0 1 .13.91v.32zm2.85-.72a1.67 1.67 0 0 0-.09-.55 1.22 1.22 0 0 0-.26-.44 1 1 0 0 0-.42-.29 1.71 1.71 0 0 0-.59-.11 1.5 1.5 0 0 0-1.08.46 1.6 1.6 0 0 0-.29.44 1.21 1.21 0 0 0-.11.49z"
            ref={wizeRef}
          />
        </svg>
        <Typography align="center" className={classNames('CLVT-message', messageClassName)} component="p" variant="h4">
          <LatexString>{message}</LatexString>
        </Typography>
      </div>
    </div>
  );
}
